'use client';

import { AddressCard } from '@/components/ui/cards';
import { useTranslations } from 'next-intl';
import { useState, useCallback, useEffect } from 'react';
import AddressForm from './form';
import { Back } from '@/components/icons';
import {
  addUpdateUserAddress,
  getAllUserAddress,
} from '@/services/user/addresses';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import useSWR from 'swr';
import PlusIcon from '@/components/icons/PlusIcon';

function Index({ defaultAddresses = [], mage }) {
  const t = useTranslations('');

  const [isViewPage, setIsViewPage] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState(null);

  const [addresses, setAddresses] = useState([...defaultAddresses]);

  const [updatingAddressId, setUpdatingAddressId] = useState(null);
  // for refetch after update the default address
  const { mutate: refetchAddresses, isLoading } = useSWR(
    'user-addresses',
    () => getAllUserAddress(mage),
    {
      revalidateOnMount: false,
      revalidateOnFocus: false,
      onSuccess: (res) => {
        setAddresses(res?.data?.data?.items || []);
      },
    }
  );

  // to update the default address
  const { trigger: updateAddress, isMutating } = useSWRMutation(
    ['/user/addresses', mage],
    // eslint-disable-next-line no-unused-vars
    ([url, mage], { arg: address }) => addUpdateUserAddress(mage, address)
  );

  const handleEdit = useCallback((address) => {
    setSelectedAddress(address);
    setIsViewPage(false);
  }, []);

  const handleDefaultAddress = useCallback(
    async (address, showMsg = true) => {
      setUpdatingAddressId(address?.id);
      try {
        await updateAddress({
          ...address,
          default_shipping: true,
          default_billing: true,
        });

        refetchAddresses();
        showMsg && toast.success(t('common.update_success_key'));
      } catch (error) {
        console.error(error.message);
        toast.error(t('user.update_failed_key'));
      } finally {
        setUpdatingAddressId(null);
      }
    },
    [updateAddress, t, refetchAddresses]
  );

  const handleAddNewAddress = useCallback(() => {
    setSelectedAddress(null);
    setIsViewPage(false);
  }, []);

  const handleBackToAddresses = useCallback(() => setIsViewPage(true), []);

  useEffect(() => {
    if (!isLoading && addresses?.length > 0) {
      const default_address = addresses.find(
        (address) => address.default_shipping && address.default_billing
      );
      if (!default_address) {
        handleDefaultAddress(addresses[0], false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, addresses]);

  return (
    <>
      {isViewPage ? (
        <>
          <h2 className='mb-4 text-xl font-bold max-lg:hidden'>
            {t('user.address_key')}
          </h2>

          <div className='grid grid-cols-1 max-lg:gap-4 max-md:gap-3.25 max-sm:gap-[9px] md:grid-cols-2 lg:grid-cols-3 lg:gap-[21px] 2xl:grid-cols-4'>
            <button
              onClick={handleAddNewAddress}
              className='hover:border-border flex w-full cursor-pointer flex-col justify-center gap-3 rounded-lg border-2 border-dashed border-gray-100 max-md:min-h-48 lg:h-[303]'
            >
              <span className='border-border mx-auto flex size-10 items-center justify-center rounded-full border-2 max-sm:size-[45px]'>
                <PlusIcon className='size-4 text-gray-700' />
              </span>
              <p className='font-medium max-lg:text-[15px] max-md:text-sm max-md:font-bold max-sm:text-xs'>
                {t('user.add_address_key')}
              </p>
            </button>

            {addresses
              .sort((a, b) => {
                const aIsDefault =
                  a.default_shipping && a.default_billing ? 1 : 0;
                const bIsDefault =
                  b.default_shipping && b.default_billing ? 1 : 0;

                return bIsDefault - aIsDefault;
              })
              .map((address) => (
                <AddressCard
                  key={address.id}
                  address={address}
                  handleEdit={handleEdit}
                  handleDefaultAddress={handleDefaultAddress}
                  isMutating={isMutating && updatingAddressId === address.id}
                  isDefault={
                    address.default_shipping && address.default_billing
                  }
                  mage={mage}
                  onDeleteSuccess={() => refetchAddresses()}
                />
              ))}
          </div>
        </>
      ) : (
        <>
          <button
            onClick={handleBackToAddresses}
            className='mb-10 flex items-center gap-2 max-lg:mb-5 max-md:mt-2'
          >
            <Back className='text-primary max-sm:size-2.5 rtl:rotate-180' />
            <span className='text-primary text-xl font-bold underline max-lg:text-lg max-md:text-base max-sm:text-sm'>
              {t('user.back_to_all_address_key')}
            </span>
          </button>

          <AddressForm
            selectedAddress={selectedAddress}
            mage={mage}
            onSuccess={() => {
              setIsViewPage(true);
              refetchAddresses();
            }}
          />
        </>
      )}
    </>
  );
}

export default Index;
